import { Component, DestroyRef, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { EmployeeDto, PaginatedResult } from '@coin/shared/core/util-models';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { StorageService } from '@coin/shared/core/data-access';
import { StorageKey } from '@coin/shared/core/util-enums';
import { GetEmulationPeopleService } from '@coin/shared/auth/data-access';
import { ConfirmationDialogComponent } from '@coin/shared/core/ui-storybook';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';

@Component({
  selector: 'coin-siemens-energy-select-emulate-person',
  templateUrl: './select-emulate-person.component.html',
  styleUrls: ['./select-emulate-person.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SelectEmulatePersonComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<SelectEmulatePersonComponent>>(MatDialogRef);
  private getEmulateableUsers = inject(GetEmulationPeopleService);
  private storageService = inject(StorageService);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  public emulateablePersons: PaginatedResult<EmployeeDto> = { content: [], total: 0 };
  public loading = false;

  public user = {
    gid: '',
    name: {
      firstname: '',
      lastname: ''
    }
  };
  public recentlyEmulated: EmployeeDto[] = [];
  public showAllRecentlyEmulated = false;

  private static readonly RECENTLY_EMULATED_COLLAPSED_COUNT = 3;

  public get displayedRecentlyEmulated(): EmployeeDto[] {
    return this.showAllRecentlyEmulated
      ? this.recentlyEmulated
      : this.recentlyEmulated.slice(0, SelectEmulatePersonComponent.RECENTLY_EMULATED_COLLAPSED_COUNT);
  }

  public get hasMoreRecentlyEmulated(): boolean {
    return this.recentlyEmulated.length > SelectEmulatePersonComponent.RECENTLY_EMULATED_COLLAPSED_COUNT;
  }

  public toggleShowAllRecentlyEmulated(): void {
    this.showAllRecentlyEmulated = !this.showAllRecentlyEmulated;
  }

  public removeRecentlyEmulated(employee: EmployeeDto, event: Event): void {
    event.stopPropagation();
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          headline: 'general.confirm',
          msg: 'general.remove-recently-emulated-confirm',
          msgValue: employee.fullName,
          confirmMsg: 'general.btnRemove',
          cancelMsg: 'general.btnCancel',
          translate: true
        }
      })
      .afterClosed()
      .pipe(
        filter(result => !!result),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.recentlyEmulated = this.recentlyEmulated.filter(entry => entry.id !== employee.id);
        this.storageService.setAsync(StorageKey.RECENTLY_EMULATED, this.recentlyEmulated);
      });
  }

  public ngOnInit(): void {
    this.loadRecentlyEmulated();
  }

  private async loadRecentlyEmulated(): Promise<void> {
    this.recentlyEmulated = (await this.storageService.getAsync(StorageKey.RECENTLY_EMULATED)) ?? [];
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public proceed(userToBeEmulated: EmployeeDto): void {
    this.storageService.setAsync(
      StorageKey.RECENTLY_EMULATED,
      [userToBeEmulated, ...this.recentlyEmulated.filter(employee => employee.id !== userToBeEmulated.id)]
    );
    this.dialogRef.close(userToBeEmulated);
  }

  public search(): void {
    this.loading = true;
    this.getEmulateableUsers
      .getEmulatePeople(this.user)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(newEmulateableUsers => {
        this.emulateablePersons = newEmulateableUsers;
        this.loading = false;
      });
  }
}
